import MapboxDraw, { DrawCustomMode, DrawCustomModeThis } from "@mapbox/mapbox-gl-draw";
import { Feature } from "geojson";
import { SnapDirectSelect } from "mapbox-gl-draw-snap-mode";

import { captureBeforeMoveFeatures, initializeValidation, validateAfterMove, ValidationState } from "fond/draw/validation/helper";
import { clickEventBBox } from "fond/utils/mapbox";

import {
  ColocatedFeature,
  filterEditableFeatures,
  findColocatedFeaturesAtLocation,
  getFeatureTarget,
  moveColocatedFeatures,
  removeGuides,
} from "./helper";

const {
  modes,
  constants: Constants,
  lib: { createSupplementaryPoints },
} = MapboxDraw;
interface DirectSelectState extends ValidationState {
  midPoints: boolean;
  featureId?: string;
  selectedCoordPaths?: string[];
  colocatedFeatures?: ColocatedFeature[] | null;
  dragMoveLocation?: { lng: number; lat: number };
  dragMoving?: boolean;
}

interface DrawCustomModeExtension extends DrawCustomMode<DirectSelectState> {
  onDrag(this: DrawCustomModeThis, state: DirectSelectState, event: MapboxDraw.MapMouseEvent): void;
}

const directSelect: DrawCustomModeExtension = {
  ...SnapDirectSelect,

  onSetup: function (opts) {
    const state = SnapDirectSelect.onSetup?.call(this, opts);
    state.colocatedFeatures = null;
    state.midPoints = this._ctx.options.midPoints !== false; // default to true if not set

    // Initialize validation
    initializeValidation.call(this, state);

    removeGuides.call(this);

    return state;
  },

  onMouseDown: function (state, event) {
    // @ts-expect-error featureTarget is possibly undefined & needs to allow for that
    event.featureTarget = getFeatureTarget.call(this, event, this._ctx.options);

    // Check if the feature can be edited before allowing any dragging
    if (event.featureTarget?.properties) {
      // For vertices and midpoints, use the parent feature ID
      const featureId =
        event.featureTarget.properties.meta === Constants.meta.VERTEX || event.featureTarget.properties.meta === Constants.meta.MIDPOINT
          ? event.featureTarget.properties.parent
          : event.featureTarget.properties.id;

      if (featureId) {
        const feature = this.getFeature(featureId);
        if (feature) {
          const editableFeatures = filterEditableFeatures(this._ctx.options, [feature.toGeoJSON() as Feature]);
          if (editableFeatures.length === 0) {
            // Feature is not editable, don't allow dragging vertices or the entire feature
            return;
          }
        }
      }
    }

    // Call base onMouseDown first to set selectedCoordPaths
    modes.direct_select.onMouseDown?.call(this, state, event);

    // Capture features for validation before vertex move
    if (state.featureId) {
      const feature = this.getFeature(state.featureId);
      if (feature) {
        captureBeforeMoveFeatures.call(this, state, [feature.toGeoJSON() as Feature]);
      }
    }

    // Find colocated features if topologicalEditing is enabled and we have a selected vertex
    if (this._ctx.options.topologicalEditing && state.featureId && state.selectedCoordPaths && state.selectedCoordPaths.length === 1) {
      const feature = this.getFeature(state.featureId);
      if (feature) {
        const coordPath = state.selectedCoordPaths[0];
        const parts = coordPath.split(".");
        const coords = feature.getCoordinates();

        let vertexCoords: [number, number] | null = null;
        if (feature.type === "Point") {
          vertexCoords = coords as [number, number];
        } else if (feature.type === "LineString") {
          const index = parseInt(parts[0], 10);
          vertexCoords = (coords as Array<[number, number]>)[index];
        } else if (feature.type === "Polygon") {
          const ringIndex = parseInt(parts[0], 10);
          const coordIndex = parseInt(parts[1], 10);
          vertexCoords = (coords as Array<Array<[number, number]>>)[ringIndex][coordIndex];
        }

        if (vertexCoords) {
          // Create a bounding box around the actual vertex coordinates
          const point = this.map.project(vertexCoords);
          const bbox = clickEventBBox({ point } as mapboxgl.MapMouseEvent);

          // Query features at this location
          const nearbyFeatures = this.featuresAt(null, bbox, "click");

          // Find colocated features at this location, filtered by editingLayerIds
          const allowedLayerIds = this._ctx.options.editOptions?.editingLayerIds;
          const colocated = findColocatedFeaturesAtLocation(vertexCoords, nearbyFeatures, state.featureId, allowedLayerIds, (id) =>
            this.getFeature(id)
          );

          state.colocatedFeatures = colocated.length > 0 ? colocated : null;
        }
      }
    }
  },

  onMouseUp: function (state, event) {
    // Validate features after vertex move
    if (state.featureId) {
      const feature = this.getFeature(state.featureId);
      if (feature) {
        validateAfterMove.call(this, state, [feature.toGeoJSON() as Feature]);
      }
    }

    // Clear colocated features after mouse up
    state.colocatedFeatures = null;
    SnapDirectSelect.onMouseUp?.call(this, state, event);
  },

  onClick: function (state, event) {
    // @ts-expect-error featureTarget is possibly undefined & needs to allow for that
    event.featureTarget = getFeatureTarget.call(this, event, this._ctx.options);
    modes.direct_select.onClick?.call(this, state, event);
  },

  onDrag: function (state, event) {
    // Get the position of the selected vertex before dragging
    let beforeCoords: [number, number] | null = null;
    if (state.featureId && state.selectedCoordPaths && state.selectedCoordPaths.length === 1) {
      const feature = this.getFeature(state.featureId);
      if (feature) {
        const coordPath = state.selectedCoordPaths[0];
        const parts = coordPath.split(".");
        const coords = feature.getCoordinates();

        // Navigate to the specific coordinate being dragged
        if (feature.type === "Point") {
          beforeCoords = coords as [number, number];
        } else if (feature.type === "LineString") {
          const index = parseInt(parts[0], 10);
          beforeCoords = (coords as Array<[number, number]>)[index];
        } else if (feature.type === "Polygon") {
          const ringIndex = parseInt(parts[0], 10);
          const coordIndex = parseInt(parts[1], 10);
          beforeCoords = (coords as Array<Array<[number, number]>>)[ringIndex][coordIndex];
        }
      }
    }

    // Call the base onDrag to move the selected vertex
    // @ts-expect-error - Type mismatch with SnapDirectSelect context
    SnapDirectSelect.onDrag?.call(this, state, event);

    // Calculate delta by comparing vertex position before and after
    let delta = { lng: 0, lat: 0 };
    if (beforeCoords && state.featureId && state.selectedCoordPaths && state.selectedCoordPaths.length === 1) {
      const feature = this.getFeature(state.featureId);
      if (feature) {
        const coordPath = state.selectedCoordPaths[0];
        const parts = coordPath.split(".");
        const coords = feature.getCoordinates();

        let afterCoords: [number, number] | null = null;
        if (feature.type === "Point") {
          afterCoords = coords as [number, number];
        } else if (feature.type === "LineString") {
          const index = parseInt(parts[0], 10);
          afterCoords = (coords as Array<[number, number]>)[index];
        } else if (feature.type === "Polygon") {
          const ringIndex = parseInt(parts[0], 10);
          const coordIndex = parseInt(parts[1], 10);
          afterCoords = (coords as Array<Array<[number, number]>>)[ringIndex][coordIndex];
        }

        if (afterCoords) {
          delta = {
            lng: afterCoords[0] - beforeCoords[0],
            lat: afterCoords[1] - beforeCoords[1],
          };
        }
      }
    }

    // Move colocated features if topologicalEditing is enabled
    if (state.colocatedFeatures && state.colocatedFeatures.length > 0 && (delta.lng !== 0 || delta.lat !== 0)) {
      moveColocatedFeatures(state.colocatedFeatures, delta, (id) => this.getFeature(id));
    }
  },

  /**
   * By default direct select will not delete the selected feature, only selected vertices.
   * Here we override that behavior so that if no vertices are selected the entire feature
   * is deleted (subject to multi-layer editing restrictions).
   */
  onTrash: function (state) {
    if (!state.featureId) return;

    const feature = this.getFeature(state.featureId);
    if (!feature) return;

    const featuresToDelete = filterEditableFeatures(this._ctx.options, [feature.toGeoJSON() as Feature]);
    if (featuresToDelete.length === 0) return;

    // Feature is editable, proceed with deletion
    if (state.selectedCoordPaths && state.selectedCoordPaths.length > 0) {
      // Vertices are selected, allow vertex deletion
      SnapDirectSelect.onTrash?.call(this, state);
    } else {
      // No vertices selected, delete the entire feature
      this.deleteFeature(state.featureId);
      this.changeMode(Constants.modes.SIMPLE_SELECT, {});
    }
  },

  toDisplayFeatures: function (state, geojson: GeoJSON.Feature<GeoJSON.LineString>, display) {
    if (state.featureId === geojson.properties?.id) {
      if (geojson.properties) {
        geojson.properties.active = Constants.activeStates.ACTIVE;
      }
      display(geojson);

      createSupplementaryPoints(geojson, {
        midpoints: state.midPoints,
        selectedPaths: state.selectedCoordPaths,
      }).forEach(display);
    } else {
      if (geojson.properties) {
        geojson.properties.active = Constants.activeStates.INACTIVE;
      }
      display(geojson);
    }
    this.fireActionable(state);
  },
};

export default directSelect;
